import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Loader2, AlertCircle, Download, Share2, Trash2, Music, Video, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  listClipsForRecording,
  deleteClip as deleteClipApi,
  updateClip as updateClipApi,
} from '../../../../services/clipsService';

const LAYOUT_LABELS = {
  gallery_view: 'Gallery',
  focus: 'Focus',
  speaker_top: 'Speaker Top',
  speaker_side: 'Speaker Side',
  audio_only: 'Audio',
};

function formatDuration(ms) {
  if (!ms || ms < 0) return '0:00';
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function StatusChip({ status, error }) {
  if (status === 'completed') return null;
  if (status === 'failed') {
    return (
      <span title={error || 'Processing failed'} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-red-300 bg-red-500/15 border border-red-500/30 rounded px-2 py-0.5">
        <AlertCircle className="w-3 h-3" /> Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded px-2 py-0.5">
      <Loader2 className="w-3 h-3 animate-spin" /> {status === 'processing' ? 'Processing' : 'Queued'}
    </span>
  );
}

function ClipRow({ clip, onDelete, onShare, onPlay, onRename }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(clip.name);
  const isReady = clip.processingStatus === 'completed' && clip.fileUrl;

  const Icon = clip.layout === 'audio_only' ? Music : Video;

  const commitRename = async () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (!trimmed || trimmed === clip.name) return;
    try {
      await onRename(clip._id, trimmed);
    } catch (err) {
      toast.error('Failed to rename clip');
      setDraft(clip.name);
    }
  };

  return (
    <div className="flex items-center gap-3 py-2.5 px-2 rounded-lg hover:bg-white/5 transition-colors group">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#6a1fbf]/15 border border-[#6a1fbf]/30 flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#a677d9]" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {editing ? (
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') { setEditing(false); setDraft(clip.name); }
              }}
              className="bg-white/10 border border-white/20 rounded px-2 py-0.5 text-sm text-white outline-none focus:border-[#6a1fbf]/60 min-w-0 flex-1"
            />
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="text-sm font-medium text-white truncate text-left hover:underline decoration-white/40 underline-offset-2"
              title="Click to rename"
            >
              {clip.name}
            </button>
          )}
          <StatusChip status={clip.processingStatus} error={clip.processingError} />
        </div>
        <div className="flex items-center gap-3 text-[11px] text-white/50 mt-0.5">
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{formatDuration(clip.totalDurationMs)}</span>
          <span>{LAYOUT_LABELS[clip.layout] || clip.layout}</span>
          {clip.segments?.length > 1 && <span>{clip.segments.length} segments</span>}
          {clip.fileSize > 0 && <span>{formatBytes(clip.fileSize)}</span>}
        </div>
      </div>

      <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {isReady && (
          <>
            <button
              onClick={() => onPlay(clip)}
              className="px-2 py-1 text-[11px] rounded bg-white/10 hover:bg-white/15 text-white"
              title="Play clip"
            >
              Play
            </button>
            <a
              href={clip.fileUrl}
              download
              onClick={(e) => e.stopPropagation()}
              className="p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-white"
              title="Download"
            >
              <Download className="w-3.5 h-3.5" />
            </a>
            <button
              onClick={() => onShare(clip)}
              className="p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-white"
              title="Share"
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>
          </>
        )}
        <button
          onClick={() => onDelete(clip)}
          className="p-1.5 rounded hover:bg-red-500/15 text-white/60 hover:text-red-300"
          title="Delete clip"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/**
 * ClipsPanel — lists clips for a parent recording, polls processing status,
 * exposes a "+ Create clip" CTA that opens the editor (handled by parent).
 *
 * Props:
 *   recordingId       — parent Recording._id
 *   onCreateClick     — fired when user clicks "+ Create clip"
 *   onPlayClip(clip)  — fired when user wants to play a finished clip
 *   onShareClip(clip) — fired when user clicks share
 *   refreshKey        — bump this from the parent after a successful save to
 *                       trigger an immediate re-fetch (avoids waiting on poll).
 */
export default function ClipsPanel({ recordingId, onCreateClick, onPlayClip, onShareClip, refreshKey = 0 }) {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollTimerRef = useRef(null);

  const fetchClips = useCallback(async () => {
    try {
      // httpServices wraps responses with `responseBody = response => response.data`,
      // so `res` is already the JSON body (no .data nesting).
      const res = await listClipsForRecording(recordingId);
      setClips(res?.clips || []);
      setError(null);
    } catch (err) {
      console.error('[ClipsPanel] Failed to load clips:', err?.response?.status, err?.response?.data, err);
      setError(err?.response?.data?.message || 'Could not load clips');
    } finally {
      setLoading(false);
    }
  }, [recordingId]);

  // Initial load + parent-triggered refresh
  useEffect(() => {
    if (!recordingId) return;
    setLoading(true);
    fetchClips();
  }, [recordingId, fetchClips, refreshKey]);

  // Poll while any clip is in progress
  const hasInFlight = useMemo(
    () => clips.some((c) => c.processingStatus === 'pending' || c.processingStatus === 'processing'),
    [clips],
  );

  useEffect(() => {
    if (!hasInFlight) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }
    pollTimerRef.current = setInterval(fetchClips, 4000);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    };
  }, [hasInFlight, fetchClips]);

  const handleDelete = async (clip) => {
    if (!window.confirm(`Delete clip "${clip.name}"?`)) return;
    try {
      await deleteClipApi(clip._id);
      setClips((prev) => prev.filter((c) => c._id !== clip._id));
      toast.success('Clip deleted');
    } catch (err) {
      console.error('[ClipsPanel] Failed to delete clip:', err);
      toast.error('Failed to delete clip');
    }
  };

  const handleRename = async (clipId, newName) => {
    const res = await updateClipApi(clipId, { name: newName });
    const updated = res?.clip;
    if (updated) {
      setClips((prev) => prev.map((c) => (c._id === clipId ? updated : c)));
      toast.success('Clip renamed');
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div className="text-xs text-white/50">
          {loading ? 'Loading…' : `${clips.length} clip${clips.length === 1 ? '' : 's'}`}
        </div>
        <button
          onClick={onCreateClick}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white text-xs font-semibold hover:opacity-90 transition-opacity"
        >
          <Plus className="w-3.5 h-3.5" />
          Create clip
        </button>
      </div>

      <div className="flex-1 overflow-y-auto -mx-2 pr-1">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-white/40">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : error ? (
          <div className="text-sm text-red-300/80 py-3">{error}</div>
        ) : clips.length === 0 ? (
          <div className="text-center py-8 px-4">
            <div className="text-sm text-white/60 mb-1">No clips yet</div>
            <div className="text-xs text-white/40">Trim a moment from this recording into a shareable clip.</div>
          </div>
        ) : (
          <div className="flex flex-col">
            {clips.map((clip) => (
              <ClipRow
                key={clip._id}
                clip={clip}
                onDelete={handleDelete}
                onShare={onShareClip}
                onPlay={onPlayClip}
                onRename={handleRename}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
